import { useAuth } from '@workos-inc/authkit-react'
import { useState } from 'react'
import { FormButton } from '../components/FormButton'
import { resetAuthState } from '../services/api-client'
import { consumeSessionId, debugDumpAuthState } from '../utils/session'

const WORKOS_CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID || ''

function useLogoutReason() {
  const [state] = useState(() => {
    const r = sessionStorage.getItem('logout_reason')
    const m = sessionStorage.getItem('logout_message')
    if (r) sessionStorage.removeItem('logout_reason')
    if (m) sessionStorage.removeItem('logout_message')
    sessionStorage.removeItem('intentional_logout')
    resetAuthState()
    return { reason: r, message: m }
  })
  return state
}

export function LoginPage() {
  const { signIn, signOut } = useAuth()
  const { reason: logoutReason } = useLogoutReason()

  const handleSignIn = async () => {
    console.log('[auth:LoginPage] handleSignIn clicked')
    debugDumpAuthState('LoginPage handleSignIn start')

    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    resetAuthState()

    const sessionId = consumeSessionId()
    console.log('[auth:LoginPage] handleSignIn — session_id from storage', { sessionId })

    if (sessionId) {
      const returnTo = encodeURIComponent(`${window.location.origin}/auth/login`)
      const logoutUrl = `https://api.workos.com/user_management/sessions/logout?client_id=${WORKOS_CLIENT_ID}&session_id=${sessionId}&return_to=${returnTo}`
      console.log('[auth:LoginPage] redirecting to WorkOS logout', logoutUrl)
      window.location.href = logoutUrl
      return
    }

    // No stored session_id — try AuthKit's signOut for active in-memory sessions.
    // If it navigates, page unloads and code below never runs.
    // If it throws or resolves without navigating, fall through to signIn directly.
    console.log('[auth:LoginPage] no stored session_id — trying AuthKit signOut')
    try {
      await signOut({
        navigate: true,
        returnTo: `${window.location.origin}/auth/login`,
      })
      console.log('[auth:LoginPage] signOut resolved without navigating — falling through to signIn')
    } catch (err) {
      console.warn('[auth:LoginPage] signOut threw (no active AuthKit session)', err)
    }

    console.log('[auth:LoginPage] calling signIn directly')
    signIn({ screenHint: 'sign-in' } as Parameters<typeof signIn>[0])
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-gray-200">
          <div className="flex flex-col items-center">
            <div className="h-14 w-14 rounded-xl bg-gray-900 flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              SupplyScanner
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Sign in to your account
            </p>
          </div>

          <div className="mt-6 space-y-4">
            {logoutReason === 'signed_out' && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                You have been signed out successfully.
              </div>
            )}
            {logoutReason === 'session_expired' && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Your session has expired. Please sign in again.
              </div>
            )}
            {logoutReason === 'account_blocked' && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                Your account is unavailable. Please contact support or sign in with a different account.
              </div>
            )}
            <FormButton
              onClick={handleSignIn}
              variant="primary"
              fullWidth
            >
              Sign in
            </FormButton>
          </div>
        </div>
      </div>
    </div>
  )
}
